import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Typography,
  Table,
  Tag,
  Progress,
  Tabs,
  List,
  Avatar,
  Timeline,
  DatePicker,
  Select,
  Input,
  Modal,
  Form,
  message,
  Tooltip,
  Badge
} from 'antd';
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  DollarOutlined,
  ShoppingCartOutlined,
  TrophyOutlined,
  CalendarOutlined,
  MessageOutlined,
  FileTextOutlined,
  ContactsOutlined,
  TeamOutlined,
  RiseOutlined,
  FallOutlined,
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  CustomerServiceOutlined,
  BellOutlined,
  StarOutlined,
  HeartOutlined,
  GlobalOutlined,
  EnvironmentOutlined,
  FunnelPlotOutlined,
  ContainerOutlined,
  ScheduleOutlined,
  BankOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

// Import CRM components
import CustomerDashboard from './CustomerDashboard';
import CustomerManagementPage from './CustomerManagementPage';
import LeadManagement from '../components/LeadManagement';
import ContactsManagement from '../components/ContactsManagement';
import OpportunityTracking from '../components/OpportunityTracking';
import ContractManagement from '../components/ContractManagement';
import AppointmentScheduling from '../components/AppointmentScheduling';
import EmailCampaignManagement from '../components/EmailCampaignManagement';
import { useIsMobile, getResponsiveColumns } from '../../../utils/responsiveUtils';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;
const { TabPane } = Tabs;

interface CRMData {
  totalCustomers: number;
  activeCustomers: number;
  totalRevenue: number;
  avgSatisfaction: number;
  recentLeads: Array<{
    id: string;
    name: string;
    email: string;
    company: string;
    source: string;
    status: 'new' | 'qualified' | 'proposal' | 'negotiation' | 'won' | 'lost';
    value: number;
    date: string;
  }>;
  upcomingActivities: Array<{
    id: string;
    type: 'call' | 'meeting' | 'email' | 'demo';
    title: string;
    customer: string;
    date: string;
    priority: 'high' | 'medium' | 'low';
  }>;
  recentCustomers: Array<{
    id: string;
    name: string;
    company: string;
    email: string;
    status: 'active' | 'inactive' | 'prospect';
    lastActivity: string;
    totalSpent: number;
  }>;
}

const CRMOverview: React.FC = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  
  const [crmData, setCrmData] = useState<CRMData>({
    totalCustomers: 0,
    activeCustomers: 0,
    totalRevenue: 0,
    avgSatisfaction: 0,
    recentLeads: [],
    upcomingActivities: [],
    recentCustomers: []
  });
  const [loading, setLoading] = useState(false);
  
  // Set active tab based on current route
  const getActiveTabFromRoute = () => {
    const path = location.pathname;
    if (path.includes('/crm/contacts')) return 'contacts';
    if (path.includes('/crm/leads')) return 'leads';
    if (path.includes('/crm/opportunities')) return 'opportunities';
    if (path.includes('/crm/contracts')) return 'contracts';
    if (path.includes('/crm/appointments')) return 'appointments';
    if (path.includes('/crm/campaigns')) return 'campaigns';
    if (path.includes('/crm/pipeline')) return 'opportunities';
    if (path.includes('/crm/customers')) return 'customers';
    return 'overview';
  };
  
  const [activeTab, setActiveTab] = useState(getActiveTabFromRoute());
  const [activityModalVisible, setActivityModalVisible] = useState(false);

  // Update active tab when route changes
  useEffect(() => {
    setActiveTab(getActiveTabFromRoute());
  }, [location.pathname]);

  useEffect(() => {
    // Mock data - replace with actual API calls
    const mockData: CRMData = {
      totalCustomers: 342,
      activeCustomers: 298,
      totalRevenue: 1250000,
      avgSatisfaction: 87.5,
      recentLeads: [
        { id: '1', name: 'Alex Johnson', email: 'alex@techstartup.com', company: 'Tech Startup Inc', source: 'Website', status: 'new', value: 25000, date: '2024-06-24' },
        { id: '2', name: 'Sarah Miller', email: 'sarah@enterprise.com', company: 'Enterprise Corp', source: 'Referral', status: 'qualified', value: 75000, date: '2024-06-23' },
        { id: '3', name: 'Mike Chen', email: 'mike@globaltech.com', company: 'Global Tech Solutions', source: 'Cold Call', status: 'proposal', value: 50000, date: '2024-06-22' },
        { id: '4', name: 'Lisa Wang', email: 'lisa@innovate.io', company: 'Innovate Systems', source: 'LinkedIn', status: 'negotiation', value: 100000, date: '2024-06-21' }
      ],
      upcomingActivities: [
        { id: '1', type: 'meeting', title: 'Product Demo with Enterprise Corp', customer: 'Sarah Miller', date: '2024-06-25T14:00:00', priority: 'high' },
        { id: '2', type: 'call', title: 'Follow-up call with Tech Startup', customer: 'Alex Johnson', date: '2024-06-25T10:30:00', priority: 'medium' },
        { id: '3', type: 'email', title: 'Send proposal to Innovate Systems', customer: 'Lisa Wang', date: '2024-06-25T16:00:00', priority: 'high' },
        { id: '4', type: 'demo', title: 'Technical demo for Global Tech', customer: 'Mike Chen', date: '2024-06-26T11:00:00', priority: 'medium' }
      ],
      recentCustomers: [
        { id: '1', name: 'John Smith', company: 'Acme Corporation', email: 'john@acme.com', status: 'active', lastActivity: '2024-06-23', totalSpent: 125000 },
        { id: '2', name: 'Emily Davis', company: 'Global Retail Ltd', email: 'emily@globalretail.com', status: 'active', lastActivity: '2024-06-22', totalSpent: 85000 },
        { id: '3', name: 'Robert Wilson', company: 'Startup XYZ', email: 'robert@startupxyz.com', status: 'prospect', lastActivity: '2024-06-21', totalSpent: 15000 }
      ]
    };
    setCrmData(mockData);
  }, []);

  const getLeadStatusColor = (status: string) => {
    const colors = {
      new: 'blue',
      qualified: 'cyan',
      proposal: 'orange',
      negotiation: 'purple',
      won: 'green',
      lost: 'red'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const getActivityTypeIcon = (type: string) => {
    const icons = {
      call: <PhoneOutlined />,
      meeting: <CalendarOutlined />,
      email: <MailOutlined />,
      demo: <CustomerServiceOutlined />
    };
    return icons[type as keyof typeof icons] || <MessageOutlined />;
  };

  const getPriorityColor = (priority: string) => {
    const colors = {
      high: 'red',
      medium: 'orange',
      low: 'green'
    };
    return colors[priority as keyof typeof colors] || 'default';
  };

  const leadsColumns: ColumnsType<any> = [
    {
      title: 'Lead',
      key: 'lead',
      render: (_, record) => (
        <div>
          <div><Text strong>{record.name}</Text></div>
          <div><Text type="secondary">{record.company}</Text></div>
          <div><Text type="secondary">{record.email}</Text></div>
        </div>
      ),
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      render: (source) => <Tag color="blue">{source}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getLeadStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (value) => <Text strong>${value.toLocaleString()}</Text>,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: () => (
        <Space>
          <Button type="link" size="small">View</Button>
          <Button type="link" size="small">Edit</Button>
        </Space>
      ),
    },
  ];

  const customersColumns: ColumnsType<any> = [
    {
      title: 'Customer',
      key: 'customer',
      render: (_, record) => (
        <div>
          <div><Text strong>{record.name}</Text></div>
          <div><Text type="secondary">{record.company}</Text></div>
          <div><Text type="secondary">{record.email}</Text></div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const color = status === 'active' ? 'green' : status === 'inactive' ? 'red' : 'orange';
        return <Tag color={color}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Total Spent',
      dataIndex: 'totalSpent',
      key: 'totalSpent',
      render: (value) => <Text strong>${value.toLocaleString()}</Text>,
    },
    {
      title: 'Last Activity',
      dataIndex: 'lastActivity',
      key: 'lastActivity',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: () => (
        <Space>
          <Button type="link" size="small">View</Button>
          <Button type="link" size="small">Contact</Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>
            <ContactsOutlined style={{ marginRight: 8 }} />
            Customer Relationship Management
          </Title>
        </Col>
        <Col>
          <Space>
            <Button type="primary" icon={<PlusOutlined />}>
              Add Lead
            </Button>
            <Button icon={<CustomerServiceOutlined />}>
              Import Contacts
            </Button>
            <Button 
              icon={<TeamOutlined />}
              onClick={() => setActiveTab('customers')}
            >
              View All Customers
            </Button>
          </Space>
        </Col>
      </Row>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Overview" key="overview">
          {/* Statistics Cards */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Customers"
                  value={crmData.totalCustomers}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Active Customers"
                  value={crmData.activeCustomers}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Revenue"
                  value={crmData.totalRevenue}
                  prefix={<DollarOutlined />}
                  precision={0}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Avg Satisfaction"
                  value={crmData.avgSatisfaction}
                  suffix="%"
                  prefix={<StarOutlined />}
                  precision={1}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={24} style={{ marginBottom: 24 }}>
            {/* Recent Leads */}
            <Col xs={24} lg={12}>
              <Card 
                title="Recent Leads" 
                extra={<Button type="link" onClick={() => setActiveTab('leads')}>View All</Button>}
              >
                <Table
                  dataSource={crmData.recentLeads}
                  columns={leadsColumns}
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>

            {/* Upcoming Activities */}
            <Col xs={24} lg={12}>
              <Card 
                title="Upcoming Activities" 
                extra={<Button type="link" onClick={() => setActiveTab('appointments')}>View Calendar</Button>}
              >
                <List
                  dataSource={crmData.upcomingActivities}
                  renderItem={(item) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<Avatar icon={getActivityTypeIcon(item.type)} />}
                        title={
                          <Space>
                            {item.title}
                            <Tag color={getPriorityColor(item.priority)}>
                              {item.priority}
                            </Tag>
                          </Space>
                        }
                        description={
                          <div>
                            <div>Customer: {item.customer}</div>
                            <div>Date: {new Date(item.date).toLocaleString()}</div>
                          </div>
                        }
                      />
                      <Button type="link" size="small">
                        View
                      </Button>
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
          </Row>

          {/* Recent Customers */}
          <Card title="Recent Customers" extra={<Button type="link" onClick={() => setActiveTab('customers')}>View All</Button>}>
            <Table
              dataSource={crmData.recentCustomers}
              columns={customersColumns}
              pagination={false}
              size="small"
            />
          </Card>
        </TabPane>

        <TabPane tab={<span><TrophyOutlined />Lead Management</span>} key="leads">
          <LeadManagement />
        </TabPane>

        <TabPane tab={<span><ContactsOutlined />Contacts</span>} key="contacts">
          <ContactsManagement />
        </TabPane>

        <TabPane tab={<span><FunnelPlotOutlined />Opportunities</span>} key="opportunities">
          <OpportunityTracking />
        </TabPane>

        <TabPane tab={<span><UserOutlined />Customer Management</span>} key="customers">
          <CustomerManagementPage />
        </TabPane>

        <TabPane tab={<span><ContainerOutlined />Contracts</span>} key="contracts">
          <ContractManagement />
        </TabPane>

        <TabPane tab={<span><CalendarOutlined />Appointments</span>} key="appointments">
          <AppointmentScheduling />
        </TabPane>

        <TabPane tab={<span><MailOutlined />Email Campaigns</span>} key="campaigns">
          <EmailCampaignManagement />
        </TabPane>

        <TabPane tab={<span><BankOutlined />Reports</span>} key="reports">
          <Row gutter={24}>
            <Col span={24}>
              <Card title="CRM Analytics & Reports" style={{ marginBottom: 24 }}>
                <Row gutter={16}>
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Statistic
                        title="Pipeline Value"
                        value={2450000}
                        prefix={<DollarOutlined />}
                        formatter={(value) => `$${Number(value).toLocaleString()}`}
                        valueStyle={{ color: '#1890ff' }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Statistic
                        title="Conversion Rate"
                        value={23.8}
                        suffix="%"
                        prefix={<TrophyOutlined />}
                        precision={1}
                        valueStyle={{ color: '#52c41a' }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Statistic
                        title="Active Contracts"
                        value={47}
                        prefix={<FileTextOutlined />}
                        valueStyle={{ color: '#722ed1' }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Statistic
                        title="Avg Deal Size"
                        value={52000}
                        prefix={<DollarOutlined />}
                        formatter={(value) => `$${Number(value).toLocaleString()}`}
                        valueStyle={{ color: '#fa8c16' }}
                      />
                    </Card>
                  </Col>
                </Row>
                <div style={{ marginTop: 24, textAlign: 'center' }}>
                  <Text type="secondary">
                    Comprehensive CRM analytics dashboard with detailed reports, charts, and insights will be displayed here.
                  </Text>
                </div>
              </Card>
            </Col>
          </Row>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default CRMOverview; 